import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Play, Trash2, Workflow, Loader2, ChevronRight, Cloud } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FREQUENCIES, STATUS_STYLES, fmtDate } from "@/lib/jobs";

const EMPTY = { system: "", name: "", frequency: "daily", enabled: true };

export default function Jobs() {
  const { user } = useAuth();
  const restricted = !!user?.restricted;
  const canWrite = !restricted && user?.can_mutate !== false;
  const [meta, setMeta] = useState({ systems: [] });
  const [connected, setConnected] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [runningId, setRunningId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    try {
      const [m, j, c] = await Promise.all([api.get("/meta"), api.get("/jobs"), api.get("/connections")]);
      setMeta(m.data);
      setJobs(j.data);
      setConnected((c.data || []).filter((x) => x.connected).map((x) => x.system));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load jobs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.system || !form.name.trim()) {
      toast.error("Please choose a system and name the job");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/jobs", {
        system: form.system,
        name: form.name.trim(),
        frequency: form.frequency,
        enabled: form.enabled,
      });
      setJobs((js) => [data, ...js]);
      toast.success("Job created");
      setDialogOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create job");
    } finally {
      setSaving(false);
    }
  };

  const runNow = async (job) => {
    setRunningId(job.id);
    try {
      const { data } = await api.post(`/jobs/${job.id}/run`);
      toast[data.status === "failed" ? "error" : "success"](
        `Run ${data.status}${data.records ? ` · ${data.records.toLocaleString()} records` : ""}`
      );
      setJobs((js) =>
        js.map((j) => (j.id === job.id ? { ...j, status: data.status, last_run_at: data.started_at } : j))
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to run job");
    } finally {
      setRunningId(null);
    }
  };

  const toggleEnabled = async (job) => {
    try {
      const { data } = await api.put(`/jobs/${job.id}`, { enabled: !job.enabled });
      setJobs((js) => js.map((j) => (j.id === job.id ? data : j)));
    } catch (e) {
      toast.error("Failed to update job");
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/jobs/${deleteId}`);
      setJobs((js) => js.filter((j) => j.id !== deleteId));
      toast.success("Job deleted");
    } catch (e) {
      toast.error("Failed to delete job");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div data-testid="jobs-page">
      <div className="mb-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Extraction</p>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight lg:text-4xl">Extraction jobs</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Schedule recurring pulls. Use Extraction for a live export now — scheduled runs keep a history here.
          </p>
        </div>
        <Button
          onClick={openNew}
          disabled={!canWrite}
          className="gap-2 font-semibold"
          data-testid="add-job-button"
        >
          <Plus className="h-4 w-4" /> New job
        </Button>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : connected.length === 0 ? (
        <div
          className="flex min-h-[320px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border/60 bg-card/40 p-10 text-center"
          data-testid="jobs-empty"
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Cloud className="h-6 w-6" />
          </div>
          <p className="mt-4 font-display text-lg font-bold">Connect a system first</p>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            Jobs run against a live org. Link Salesforce, Zoho or Oracle — then come back and I'll help you schedule the first export.
          </p>
          <Button asChild className="mt-5 font-semibold" data-testid="jobs-empty-connect">
            <Link to="/dashboard/connections">Connect a system</Link>
          </Button>
        </div>
      ) : jobs.length === 0 ? (
        <div
          className="flex min-h-[320px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border/60 bg-card/40 p-10 text-center"
          data-testid="jobs-empty"
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Workflow className="h-6 w-6" />
          </div>
          <p className="mt-4 font-display text-lg font-bold">No jobs yet</p>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            Create a scheduled job, or run a one-off export from Extraction first.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Button onClick={openNew} disabled={!canWrite} className="gap-2 font-semibold" data-testid="jobs-empty-new">
              <Plus className="h-4 w-4" /> New job
            </Button>
            <Button asChild variant="outline">
              <Link to="/dashboard/extraction">Open Extraction</Link>
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="jobs-list">
          {jobs.map((job) => (
            <div
              key={job.id}
              className="flex flex-col gap-4 rounded-xl border border-border bg-card p-5 transition-colors duration-200 hover:border-primary/30 sm:flex-row sm:items-center sm:justify-between"
              data-testid={`job-item-${job.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Workflow className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/dashboard/jobs/${job.id}`}
                      className="font-display text-base font-bold tracking-tight hover:text-primary"
                      data-testid={`job-link-${job.id}`}
                    >
                      {job.name}
                    </Link>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${STATUS_STYLES[job.status] || STATUS_STYLES.idle}`}
                      data-testid={`job-status-${job.id}`}
                    >
                      {job.status}
                    </span>
                  </div>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    {job.system_name} · <span className="capitalize">{job.frequency}</span> · Last run {fmtDate(job.last_run_at)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 sm:gap-3">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={job.enabled}
                    onCheckedChange={() => toggleEnabled(job)}
                    disabled={!canWrite}
                    data-testid={`job-toggle-${job.id}`}
                  />
                  <span className="hidden text-xs text-muted-foreground sm:inline">
                    {job.enabled ? "Scheduled" : "Paused"}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  disabled={!canWrite || runningId === job.id}
                  onClick={() => runNow(job)}
                  data-testid={`job-run-${job.id}`}
                >
                  {runningId === job.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                  <span className="hidden sm:inline">Run now</span>
                </Button>
                <Link to={`/dashboard/jobs/${job.id}`}>
                  <Button variant="outline" size="icon" className="h-9 w-9" data-testid={`job-view-${job.id}`}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 text-destructive hover:text-destructive"
                  onClick={() => setDeleteId(job.id)}
                  disabled={!canWrite}
                  data-testid={`job-delete-${job.id}`}
                  aria-label="Delete job"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="job-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">New extraction job</DialogTitle>
            <DialogDescription>Pick a connected system and how often it should run.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Connected system</Label>
              <Select value={form.system} onValueChange={(v) => setForm((f) => ({ ...f, system: v }))}>
                <SelectTrigger data-testid="job-system-select">
                  <SelectValue placeholder="Select a system" />
                </SelectTrigger>
                <SelectContent>
                  {(connected.length ? meta.systems.filter((s) => connected.includes(s.id)) : meta.systems).map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`job-system-option-${s.id}`}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="job-name">Job name</Label>
              <Input
                id="job-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Salesforce nightly extract"
                className="h-11"
                data-testid="job-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Schedule</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm((f) => ({ ...f, frequency: v }))}>
                <SelectTrigger data-testid="job-frequency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCIES.map((fq) => (
                    <SelectItem key={fq.id} value={fq.id} data-testid={`job-frequency-option-${fq.id}`}>
                      {fq.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border bg-muted/40 px-4 py-3">
              <div>
                <p className="text-sm font-medium">Enabled</p>
                <p className="text-xs text-muted-foreground">Turn off to pause the schedule.</p>
              </div>
              <Switch
                checked={form.enabled}
                onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
                data-testid="job-enabled-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="job-cancel">
              Cancel
            </Button>
            <Button onClick={save} disabled={saving} className="font-semibold" data-testid="job-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create job"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent data-testid="job-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this job?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the job and its run history. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="job-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="job-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
